/**
 * 格式化文件大小
 * @param {number} bytes - 文件大小(字節)
 * @returns {string} 格式化後的文件大小
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * 🆕 檢查文件大小是否超過限制
 * @param {File} file - 文件對象
 * @param {string} fileType - 文件類型 ('image' 或 'pdf')
 * @returns {object} 檢查結果 {isValid, message, size}
 */
export const checkFileSize = (file, fileType = 'image') => {
  const MAX_IMAGE_SIZE = 50 * 1024 * 1024; // 50MB
  const MAX_PDF_SIZE = 100 * 1024 * 1024; // 100MB
  const WARNING_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB 警告
  const WARNING_PDF_SIZE = 20 * 1024 * 1024; // 20MB 警告

  const maxSize = fileType === 'pdf' ? MAX_PDF_SIZE : MAX_IMAGE_SIZE;
  const warningSize = fileType === 'pdf' ? WARNING_PDF_SIZE : WARNING_IMAGE_SIZE;

  if (file.size > maxSize) {
    return {
      isValid: false,
      level: 'error',
      message: `文件過大！最大支持 ${formatFileSize(maxSize)}，您的文件為 ${formatFileSize(file.size)}`,
      size: file.size
    };
  }

  if (file.size > warningSize) {
    return {
      isValid: true,
      level: 'warning',
      message: `文件較大（${formatFileSize(file.size)}），處理可能需要較長時間`,
      size: file.size
    };
  }

  return {
    isValid: true,
    level: 'success',
    message: '文件大小正常',
    size: file.size
  };
};

/**
 * 🆕 計算預估處理時間
 * @param {number} fileSize - 文件大小（字節）
 * @param {string} operation - 操作類型
 * @returns {number} 預估時間（秒）
 */
export const estimateProcessingTime = (fileSize, operation = 'compress') => {
  // 基於文件大小的處理速度估算（字節/秒）
  const speeds = {
    compress: 2 * 1024 * 1024, // 2MB/s
    convert: 3 * 1024 * 1024, // 3MB/s
    resize: 4 * 1024 * 1024, // 4MB/s
    pdf: 1 * 1024 * 1024 // 1MB/s (PDF 較慢)
  };

  const speed = speeds[operation] || speeds.compress;
  const estimatedSeconds = Math.ceil(fileSize / speed);

  // 最少 1 秒，最多 300 秒（5 分鐘）
  return Math.max(1, Math.min(estimatedSeconds, 300));
};

/**
 * 🆕 格式化剩餘時間
 * @param {number} seconds - 剩餘秒數
 * @returns {string} 格式化的時間字符串
 */
export const formatRemainingTime = (seconds) => {
  if (seconds < 1) return '即將完成...';
  if (seconds < 60) return `約 ${Math.ceil(seconds)} 秒`;
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.ceil(seconds % 60);
  
  if (remainingSeconds === 0) {
    return `約 ${minutes} 分鐘`;
  }
  
  return `約 ${minutes} 分 ${remainingSeconds} 秒`;
};

/**
 * 🆕 計算處理速度
 * @param {number} processedBytes - 已處理字節數
 * @param {number} elapsedSeconds - 已用時間（秒）
 * @returns {string} 處理速度字符串
 */
export const calculateProcessingSpeed = (processedBytes, elapsedSeconds) => {
  if (elapsedSeconds === 0) return '計算中...';
  
  const bytesPerSecond = processedBytes / elapsedSeconds;
  return `${formatFileSize(bytesPerSecond)}/s`;
};

/**
 * 解析頁面範圍字符串
 * @param {string} rangeStr - 範圍字符串(例如:"1-3,5,8-10")
 * @param {number} maxPages - 最大頁數
 * @returns {number[]} 頁碼數組
 */
export const parsePageRange = (rangeStr, maxPages) => {
  const pages = [];
  const ranges = rangeStr.split(',');

  for (let i = 0; i < ranges.length; i++) {
    const range = ranges[i].trim();

    // 處理單個頁碼
    if (/^\d+$/.test(range)) {
      const page = parseInt(range);
      if (page > 0 && page <= maxPages) {
        pages.push(page);
      }
    }
    // 處理範圍
    else if (/^\d+-\d+$/.test(range)) {
      const parts = range.split('-');
      const start = parseInt(parts[0]);
      const end = parseInt(parts[1]);

      if (start > 0 && end <= maxPages && start <= end) {
        for (let j = start; j <= end; j++) {
          pages.push(j);
        }
      }
    }
  }

  // 去重並排序
  return [...new Set(pages)].sort((a, b) => a - b);
};

/**
 * 創建下載鏈接並觸發下載 - 修復版本
 * @param {Blob|string} data - 要下載的數據(Blob 或 URL)
 * @param {string} filename - 文件名
 */
export const downloadFile = (data, filename) => {
  try {
    let url;
    let shouldRevoke = false;

    // 如果傳入的是 Blob,創建 URL
    if (data instanceof Blob) {
      url = URL.createObjectURL(data);
      shouldRevoke = true;
    }
    // 如果傳入的是 blob: URL
    else if (typeof data === 'string' && data.startsWith('blob:')) {
      url = data;
      shouldRevoke = false; // 不要撤銷外部創建的 URL
    }
    // 如果傳入的是普通 URL
    else if (typeof data === 'string') {
      url = data;
      shouldRevoke = false;
    } else {
      throw new Error('無效的數據類型');
    }

    // 創建下載鏈接
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();

    // 清理
    setTimeout(() => {
      document.body.removeChild(a);
      if (shouldRevoke) {
        URL.revokeObjectURL(url);
      }
    }, 100);

    if (window.showToast) {
      window.showToast('下載已開始', 'success');
    }
  } catch (error) {
    console.error('下載文件錯誤:', error);
    if (window.showToast) {
      window.showToast('下載失敗: ' + error.message, 'error');
    }
  }
};

/**
 * 批量下載文件
 * @param {Array} files - 文件數組 [{blob, filename}]
 */
export const downloadMultipleFiles = (files) => {
  files.forEach((file, index) => {
    setTimeout(() => {
      downloadFile(file.blob, file.filename);
    }, index * 200); // 每個文件間隔 200ms
  });
};

/**
 * 檢測文件類型
 * @param {File} file - 文件對象
 * @returns {object} 文件類型信息
 */
export const detectFileType = (file) => {
  const isPdf = file.type === 'application/pdf';
  const isImage = file.type.startsWith('image/');

  let format = 'Unknown';
  if (isPdf) {
    format = 'PDF';
  } else if (file.type === 'image/jpeg') {
    format = 'JPG';
  } else if (file.type === 'image/png') {
    format = 'PNG';
  } else if (file.type === 'image/webp') {
    format = 'WEBP';
  } else if (file.type === 'image/gif') {
    format = 'GIF';
  }

  return {
    isPdf,
    isImage,
    format,
    size: file.size,
    name: file.name
  };
};

/**
 * 🆕 清理記憶體中的 Blob URLs
 * @param {Array<string>} urls - 要清理的 URL 數組
 */
export const cleanupBlobUrls = (urls) => {
  if (!Array.isArray(urls)) {
    urls = [urls];
  }

  urls.forEach(url => {
    if (url && typeof url === 'string' && url.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(url);
        console.log('已釋放 Blob URL:', url.substring(0, 50) + '...');
      } catch (error) {
        console.warn('釋放 Blob URL 失敗:', error);
      }
    }
  });
};

/**
 * 🆕 獲取記憶體使用情況（如果瀏覽器支持）
 * @returns {object|null} 記憶體信息
 */
export const getMemoryInfo = () => {
  if (performance.memory) {
    return {
      used: formatFileSize(performance.memory.usedJSHeapSize),
      total: formatFileSize(performance.memory.totalJSHeapSize),
      limit: formatFileSize(performance.memory.jsHeapSizeLimit),
      usagePercent: Math.round((performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit) * 100)
    };
  }
  return null;
};